<!-- src/views/admin/AdminDocumentEditorView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24 text-left">

        <!-- HEADER & ACTIONS: Midnight Premium Standard -->
        <div class="flex flex-col xl:flex-row items-start xl:items-center justify-between mb-12 gap-6 bg-[#0A1630] p-10 rounded-[40px] shadow-2xl border-b-4 border-[#20FCF1] animate-fadeIn">
          <div>
            <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">{{ isEditing ? 'Edit Document Protocol' : 'Create Document Protocol' }}</h1>
            <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-3">Master Document Configuration • Sentinel Safe Premium</p>
          </div>
          
          <div class="flex flex-wrap gap-3 w-full xl:w-auto items-center">
              <!-- 🛡️ NAVIGATION STANDARDISED: Uniform Return back button layout and matching vertical padding -->
              <button type="button" @click="router.push({ name: 'AdminManageDocuments' })" class="px-6 py-3.5 bg-white/5 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all flex items-center gap-2 cursor-pointer">
                 <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return</span>
              </button>
          </div>
        </div>

        <!-- Main Form Content -->
        <div class="bg-white p-8 md:p-12 rounded-[40px] border border-slate-100 shadow-2xl text-left animate-fadeIn">
          <form @submit.prevent="handleSubmit" class="space-y-10 mt-2 text-left">
            <section class="text-left">
              <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight mb-8 border-b pb-4 text-left">Document Details</h2>
              
              <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 text-left">
                
                <!-- Document Type Dropdown -->
                <div class="sm:col-span-1 text-left">
                  <label for="document-type" class="micro-label mb-2 block ml-1 text-left">Document Category <span class="text-rose-500">*</span></label>
                  
                  <select 
                    id="document-type" 
                    v-model="formData.type" 
                    required 
                    class="form-select"
                    @change="handleCategoryChange"
                  >
                    <option disabled value="">Select a category</option>
                    <option v-for="category in availableCategories" :key="category" :value="category">
                      {{ category }}
                    </option>
                    <option value="___ADD_NEW___" class="font-black text-[#FD5E00]">
                      -- Add New Category --
                    </option>
                  </select>

                  <!-- Inline Add New Category Input -->
                  <div v-if="showAddCategoryInput" class="mt-4 p-5 bg-slate-50 border border-slate-200 rounded-[24px] animate-fade-in text-left">
                    <label class="micro-label mb-2 block ml-1 text-left">New Category Name</label>
                    <div class="flex gap-2 text-left">
                      <input 
                        type="text" 
                        v-model="newCategoryName" 
                        ref="newCategoryInput"
                        class="form-input block w-full focus:ring-2 focus:ring-[#20FCF1]"
                        placeholder="e.g. Manuals"
                        @keydown.enter.prevent="saveNewCategory"
                      />
                      <button 
                        type="button" 
                        @click="saveNewCategory"
                        :disabled="!newCategoryName"
                        class="bg-[#0A1630] text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 disabled:opacity-50 cursor-pointer border-none"
                      >
                        Save
                      </button>
                      <button 
                        type="button" 
                        @click="cancelAddCategory"
                        class="bg-slate-100 text-slate-500 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 cursor-pointer border-none"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
                
                <!-- Document Reference -->
                <div class="sm:col-span-1 text-left">
                  <label for="document-reference" class="micro-label mb-2 block ml-1 text-left">Reference</label>
                  <input type="text" id="document-reference" v-model="formData.reference" class="form-input" placeholder="e.g. POL-001-V2" />
                </div>

                <!-- Document Title -->
                <div class="sm:col-span-2 text-left">
                  <label for="document-title" class="micro-label mb-2 block ml-1 text-left">Title <span class="text-rose-500">*</span></label>
                  <input type="text" id="document-title" v-model="formData.title" required class="form-input" placeholder="e.g. Health & Safety Policy" />
                </div>

                <!-- Document Content (Rich Text Editor) -->
                <div class="sm:col-span-2 text-left">
                  <label class="micro-label mb-3 block ml-1 text-left">Content <span class="text-rose-500">*</span></label>
                  <div class="editor-container rounded-2xl overflow-hidden border border-slate-200 shadow-inner">
                    <QuillEditor 
                      v-model:content="formData.content" 
                      contentType="html" 
                      theme="snow" 
                      :toolbar="editorToolbar"
                      placeholder="Paste your document content here..."
                    />
                  </div>
                </div>
                
                <!-- Document Footer Text (Upgraded to rich text editor with brand colour options) -->
                <div class="sm:col-span-2 text-left">
                  <label class="micro-label mb-3 block ml-1 text-left">Footer Text <span class="text-[10px] text-slate-400 font-normal">(Double-click text to colourise or style)</span></label>
                  <div class="editor-container rounded-2xl overflow-hidden border border-slate-200 shadow-inner !h-[120px]">
                    <QuillEditor 
                      v-model:content="formData.footerText" 
                      contentType="html" 
                      theme="snow" 
                      :toolbar="footerToolbar"
                      placeholder="e.g. BMS - Doc Ref: POL-001 - Version: 1"
                    />
                  </div>
                </div>

              </div>
            </section>

            <!-- Action Buttons -->
            <div class="pt-6 border-t border-slate-100 flex flex-col sm:flex-row justify-end gap-3 text-left">
              <button 
                type="button" 
                @click="router.push({ name: 'AdminManageDocuments' })" 
                class="px-8 py-4 bg-slate-100 text-slate-500 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-200 transition-all cursor-pointer border-none text-center"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                :disabled="isSubmitting" 
                class="px-8 py-4 bg-[#20FCF1] text-[#0A1630] rounded-2xl font-black uppercase text-[10px] tracking-widest hover:scale-105 active:scale-95 transition-all shadow-xl disabled:opacity-50 cursor-pointer border-none text-center"
              >
                <span v-if="isSubmitting">Saving...</span>
                <span v-else>Save Document</span>
              </button>
            </div>
            <p v-if="submissionError" class="mt-4 text-xs font-black uppercase tracking-wider text-rose-600 text-right">{{ submissionError }}</p>
          </form>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onUnmounted, computed, nextTick, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db, firebaseApp } from '@/firebaseConfig';
import { useUserStore } from '@/stores/userStore';
import { ArrowUturnLeftIcon } from '@heroicons/vue/24/outline';

// Rich Text Editor Imports
import { Quill, QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

// Register custom size formats to support Small and Extra Small options
const Size = Quill.import('formats/size');
Size.whitelist = ['extra-small', 'small', 'large', 'huge'];
Quill.register(Size, true);

const router = useRouter();
const route = useRoute();
const functions = getFunctions(firebaseApp, 'europe-west2');
const userStore = useUserStore();

const documentId = ref(route.params.id || null);
const isEditing = computed(() => !!documentId.value);

// Dynamic Categories State
const availableCategories = ref(['Policies', 'Procedures']); 
const showAddCategoryInput = ref(false);
const newCategoryName = ref('');
const newCategoryInput = ref(null);

const formData = reactive({
  title: '',
  content: '', // Stores HTML string
  type: '',
  reference: '', 
  footerText: '', 
});

// Quill Editor Toolbar Configuration (With custom size whitelists)
const editorToolbar = [
  [{ 'header': [1, 2, 3, 4, false] }],
  [{ 'size': ['extra-small', 'small', false, 'large', 'huge'] }],
  ['bold', 'italic', 'underline', 'strike'],        
  [{ 'color': [] }, { 'background': [] }],          
  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
  [{ 'align': [] }],
  ['clean']                                         
];

// Mini-toolbar designed specifically for custom footer branding colour selections
const footerToolbar = [
  ['bold', 'italic'],
  [{ 'color': [] }, { 'background': [] }],
  ['clean']
];

const isSubmitting = ref(false);
const submissionError = ref('');

let headerTimerId = null;

// 🛡️ RECONCILED IDENTITY RESOLUTION: Secure computed client tracking prevents race condition crashes
const currentClientId = computed(() => userStore.clientId || userStore.userProfile?.clientId);

// --- Category Logic ---

const fetchCategories = async () => {
  try {
    const clientId = currentClientId.value;
    if (!clientId) return;
    const docRef = doc(db, 'clients', clientId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data.documentCategories && Array.isArray(data.documentCategories)) {
        availableCategories.value = data.documentCategories;
      }
    }
    // Set default selection if none selected and not "Add New"
    if (!formData.type && availableCategories.value.length > 0) {
      formData.type = availableCategories.value[0];
    }
  } catch (error) {
    console.error("Error fetching categories:", error);
  }
};

const handleCategoryChange = () => {
  if (formData.type === '___ADD_NEW___') {
    showAddCategoryInput.value = true;
    nextTick(() => {
      if (newCategoryInput.value) newCategoryInput.value.focus();
    });
  } else {
    showAddCategoryInput.value = false;
  }
};

const saveNewCategory = async () => {
  const clientId = currentClientId.value;
  const categoryName = newCategoryName.value.trim();
  if (!categoryName || !clientId) return;

  // Don't duplicate
  if (availableCategories.value.includes(categoryName)) {
    formData.type = categoryName;
    showAddCategoryInput.value = false;
    newCategoryName.value = '';
    return;
  }

  try {
    // Save to Firestore
    const docRef = doc(db, 'clients', clientId);
    await updateDoc(docRef, {
      documentCategories: arrayUnion(categoryName)
    });

    // Update Local State
    availableCategories.value.push(categoryName);
    formData.type = categoryName; // Select the new category
    
    // Reset UI
    showAddCategoryInput.value = false;
    newCategoryName.value = '';

  } catch (error) {
    console.error("Error saving new category:", error);
    alert("Failed to save the new category. Please try again.");
  }
};

const cancelAddCategory = () => {
  showAddCategoryInput.value = false;
  newCategoryName.value = '';
  // Revert selection to the first available item or empty
  if (availableCategories.value.length > 0) {
    formData.type = availableCategories.value[0];
  } else {
    formData.type = '';
  }
};

// --- Document Logic ---

const fetchDocumentData = async () => {
  const clientId = currentClientId.value;
  if (!isEditing.value || !clientId) return;

  try {
    const docRef = doc(db, 'clients', clientId, 'documents', documentId.value);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      // Use direct assignment on the reactive object to ensure reliable loading
      Object.assign(formData, {
        title: data.title,
        content: data.content,
        type: data.type,
        reference: data.reference || '', 
        footerText: data.footerText || '', 
      });
      
      if (data.type && !availableCategories.value.includes(data.type)) {
        availableCategories.value.push(data.type);
      }
    } else {
      submissionError.value = "Document not found.";
    }
  } catch (error) {
    console.error("Error fetching document data:", error);
    submissionError.value = "Failed to load document data.";
  }
};

const handleSubmit = async () => {
  submissionError.value = '';
  
  if (!formData.title || !formData.content || !formData.type) {
    submissionError.value = "Please fill in all fields.";
    return;
  }
  isSubmitting.value = true;

  try {
    const managePolicy = httpsCallable(functions, 'managePolicy');
    const action = isEditing.value ? 'update' : 'create';
    const payload = { ...formData };
    if (isEditing.value) {
      payload.id = documentId.value;
    }
    
    const result = await managePolicy({ action: action, policyData: payload });

    if (result && result.data.success) {
      router.push({ name: 'AdminManageDocuments' });
    } else {
      throw new Error('The function did not return a success status.');
    }

  } catch (error) {
    console.error("Error saving document:", error);
    submissionError.value = `Failed to save document. Please try again.`;
  } finally {
    isSubmitting.value = false;
  }
};

// 🛡️ RECONCILED SYNC: Watches computed client ID reactively to resolve race condition crashes
watch(currentClientId, async (newId) => {
  if (newId) {
    await fetchCategories();
    await fetchDocumentData();
  }
}, { immediate: true });

onUnmounted(() => {
  if (headerTimerId) { clearInterval(headerTimerId); }
});
</script>

<style scoped>
.micro-label { @apply text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 block ml-1 text-left; }
.form-input { @apply block w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold uppercase transition-all focus:ring-2 focus:ring-[#20FCF1] outline-none shadow-inner; }
.form-select {
  @apply block w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold uppercase transition-all focus:ring-2 focus:ring-[#20FCF1] outline-none shadow-inner cursor-pointer appearance-none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%230A1630'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1.25rem center;
  background-size: 1.25rem;
}
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

/* Editor Styles */
.editor-container {
  height: 400px;
  background: white;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
:deep(.ql-toolbar) {
  border-top: none !important;
  border-left: none !important;
  border-right: none !important;
  border-bottom: 1px solid #e2e8f0 !important;
  background-color: #f8fafc;
}
:deep(.ql-container) {
  border: none !important;
  flex-grow: 1;
  overflow-y: auto;
}
:deep(.ql-editor) {
  min-height: 200px;
  font-family: inherit;
  font-size: 0.875rem;
}

/* Custom Size Picker Labels Mapping inside Quill editor */
:deep(.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="extra-small"]::before),
:deep(.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="extra-small"]::before) {
  content: 'Extra Small' !important;
}
:deep(.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="small"]::before),
:deep(.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="small"]::before) {
  content: 'Small' !important;
}
:deep(.ql-snow .ql-picker.ql-size .ql-picker-label::before),
:deep(.ql-snow .ql-picker.ql-size .ql-picker-item::before) {
  content: 'Normal' !important;
}
:deep(.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="large"]::before),
:deep(.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="large"]::before) {
  content: 'Large' !important;
}
:deep(.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="huge"]::before),
:deep(.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="huge"]::before) {
  content: 'Huge' !important;
}

/* Dynamic styling rules for sizing outputs */
:deep(.ql-editor .ql-size-extra-small) {
  font-size: 0.75rem !important;
}
:deep(.ql-editor .ql-size-small) {
  font-size: 0.875rem !important;
}
</style>